import { useEffect } from 'react';
import { css } from '@emotion/react';
import { Button } from 'react-bootstrap';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import t from 'react-translate';

import NvRadioButton from 'shared/components/inputs/nv-radio-button';
import NvModal, { ModalType } from 'shared/components/nv-modal';
import { InsightVisibility } from 'redux/schemas/models/exercise';
import { gray7, primary } from 'styles/global_defaults/colors';
import { textLargeLineHeight } from 'styles/global_defaults/fonts';
import { largeSpacing, quarterSpacing } from 'styles/global_defaults/scaffolding';

const modalStyles = css`
  .bs4-modal-body {
    padding: ${largeSpacing}px !important;
    padding-top: 0 !important;
  }
`;

const styles = css`
  .selection {
    flex-grow: 1;
    .option {
      border-radius: ${quarterSpacing}px;
      background-color: ${gray7};
      label {
        cursor: pointer !important;
        line-height: ${textLargeLineHeight}px;
      }
      &.selected {
        border: 1px solid ${primary};
        border-radius: ${quarterSpacing}px;
        label {
          color: ${primary};
          ::before {
            border-color: ${primary};
            background-color: ${primary};
          }
        }
      }
    }
  }
`;

type AiInsightsSetupModalProps = {
  show: boolean;
  onClose: () => void;
  onSave: (insightVisibility: InsightVisibility) => void;
  currentVisibility: InsightVisibility;
  courseAliases: Record<string, string>;
};

type FormValues = {
  insightVisibility: string;
};

const AiInsightsSetupModalBody = ({
  onClose,
  onSave,
  courseAliases,
}: Omit<AiInsightsSetupModalProps, 'show' | 'currentVisibility'>) => {
  const translations = t.LECTURE_PAGES.COMPONENTS.AI_INSIGHTS;
  const selectedValue = useWatch<FormValues>({ name: 'insightVisibility' });

  const options = [
    { value: InsightVisibility.DISABLED, label: translations.OPTION_DISABLED(), dataQa: 'ai-insights-option-disabled' },
    { value: InsightVisibility.LEARNERS, label: translations.OPTION_DIRECT(courseAliases), dataQa: 'ai-insights-option-learners' },
    { value: InsightVisibility.ADMINS, label: translations.OPTION_ADMINS(courseAliases), dataQa: 'ai-insights-option-admins' },
  ];

  return (
    <div css={styles} className='d-flex align-items-center flex-column w-100 h-100' data-qa='ai-insights-setup-modal'>
      <p className='text-regular mt-5'>
        {translations.MODAL_DESCRIPTION()}
      </p>
      <div className='w-100 mb-4 ml-5 d-flex flex-column justify-content-center' role='radiogroup' aria-label={translations.MODAL_TITLE()}>
        {options.map((option) => (
          <NvRadioButton
            withForm
            key={option.value}
            value={option.value}
            name='insightVisibility'
            label={option.label}
            ariaLabel={option.label}
            data-qa={option.dataQa}
          />
        ))}
      </div>
      <div className='buttons-row mt-4'>
        <Button
          variant='secondary'
          className='mr-2'
          onClick={onClose}
          aria-label={t.FORM.CANCEL()}
          data-qa='ai-insights-setup-cancel'
        >
          {t.FORM.CANCEL()}
        </Button>
        <Button
          variant='primary'
          onClick={() => onSave(selectedValue as InsightVisibility)}
          aria-label={t.FORM.SAVE()}
          data-qa='ai-insights-setup-save'
          disabled={selectedValue === undefined || selectedValue === null}
        >
          {t.FORM.SAVE()}
        </Button>
      </div>
    </div>
  );
};

const AiInsightsSetupModal = (props: AiInsightsSetupModalProps) => {
  const { show, onClose, onSave, currentVisibility, courseAliases } = props;
  const translations = t.LECTURE_PAGES.COMPONENTS.AI_INSIGHTS;

  const methods = useForm<FormValues>({
    defaultValues: {
      insightVisibility: currentVisibility,
    },
  });

  useEffect(() => {
    methods.reset({ insightVisibility: currentVisibility });
  }, [currentVisibility, methods]);

  return (
    <NvModal
      show={show}
      header={translations.MODAL_TITLE()}
      modalStyles={modalStyles}
      height={380}
      width={536}
      type={ModalType.FIXED}
      onClose={onClose}
      body={(
        <FormProvider {...methods}>
          <AiInsightsSetupModalBody
            onClose={onClose}
            onSave={onSave}
            courseAliases={courseAliases}
          />
        </FormProvider>
      )}
    />
  );
};

export default AiInsightsSetupModal;
